// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';

import { notSoldReasonOptions } from 'utils/constants';
import {
    buildStatusChange,
    getInitialStatusForm,
    getInvoiceProductOptions,
    validateStatusForm,
} from './orderStatus.helpers';

const TITLES = {
    followUp: { title: 'Følg op på senere', subtitle: 'Opret en opgave på den aktuelle ordre' },
    completed: { title: 'Send faktura (afsluttet)', subtitle: 'Fakturerer rådgivning' },
    cancelled: { title: 'Annullér ordre', subtitle: 'Bekræfter du, at kunden ønsker at annullere sin ordre?' },
    notSold: { title: 'Ikke blevet til salg', subtitle: 'Hvorfor blev det ikke til et salg? Vælg mindst én årsag.' },
};

// advisors: [{ userId, name }]
function OrderStatusDialog({ status, order, customerName, customerEmail, assignedTo, advisors, onSubmit, onClose }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!status) return;
        setForm(getInitialStatusForm({ order, customerName, customerEmail, assignedTo }));
        setError(null);
    }, [status, order, customerName, customerEmail, assignedTo]);

    if (!status || !form) return null;

    const productOptions = getInvoiceProductOptions(order);
    const validationError = validateStatusForm(status, form);
    const { title, subtitle } = TITLES[status];

    const setSection = (section, field, value) => {
        setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    };

    const toggleReason = (reason, checked) => {
        setForm(prev => ({
            ...prev,
            notSoldReasons: checked
                ? [...prev.notSoldReasons, reason]
                : prev.notSoldReasons.filter(value => value !== reason),
        }));
    };

    const selectProduct = (productId) => {
        const product = productOptions.find(option => option.productId === productId);
        setForm(prev => ({
            ...prev,
            invoice: { ...prev.invoice, productId, amount: product ? String(product.price) : prev.invoice.amount },
        }));
    };

    const handleSubmit = async () => {
        if (validationError) return;
        setSaving(true);
        setError(null);
        try {
            await onSubmit(buildStatusChange({ status, form, order, advisors }));
        } catch (submitError) {
            setError(submitError?.response?.data?.reason || 'Kunne ikke gemme status');
        } finally {
            setSaving(false);
        }
    };

    const submitLabel = {
        followUp: 'Gem opfølgning',
        // No Billy integration yet: the invoice details are only saved on the order.
        completed: 'Gem som afsluttet',
        cancelled: 'Ja, annullér ordre',
        notSold: 'Gem',
    }[status];

    return (
        <Dialog open onClose={ saving ? undefined : onClose } fullWidth maxWidth="sm">
            <DialogTitle>{ title }</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>{ subtitle }</DialogContentText>

                { status === 'followUp' && (
                    <Stack spacing={ 2 }>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 2 }>
                            <TextField
                                label="Opfølgningsdato"
                                type="date"
                                value={ form.followUp.date }
                                onChange={ e => setSection('followUp', 'date', e.target.value) }
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <FormControl fullWidth>
                                <InputLabel id="follow-up-advisor-label">Ansvarlig rådgiver</InputLabel>
                                <Select
                                    labelId="follow-up-advisor-label"
                                    label="Ansvarlig rådgiver"
                                    value={ form.followUp.advisorId }
                                    onChange={ e => setSection('followUp', 'advisorId', e.target.value) }
                                >
                                    <MenuItem value="">Ingen</MenuItem>
                                    { advisors.map(advisor => (
                                        <MenuItem key={ advisor.userId } value={ advisor.userId }>{ advisor.name }</MenuItem>
                                    )) }
                                </Select>
                            </FormControl>
                        </Stack>
                        <TextField
                            label="Opgavetitel"
                            value={ form.followUp.title }
                            onChange={ e => setSection('followUp', 'title', e.target.value) }
                            fullWidth
                        />
                        <TextField
                            label="Noter"
                            value={ form.followUp.notes }
                            onChange={ e => setSection('followUp', 'notes', e.target.value) }
                            multiline
                            minRows={ 3 }
                            fullWidth
                        />
                        <FormControlLabel
                            control={ <Checkbox checked={ form.followUp.addToCalendar } onChange={ e => setSection('followUp', 'addToCalendar', e.target.checked) } /> }
                            label="Læg i kalender"
                        />
                    </Stack>
                ) }

                { status === 'completed' && (
                    <Stack spacing={ 2 }>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 2 }>
                            <TextField
                                label="Navn"
                                value={ form.invoice.name }
                                onChange={ e => setSection('invoice', 'name', e.target.value) }
                                fullWidth
                            />
                            <TextField
                                label="E-mail"
                                type="email"
                                value={ form.invoice.email }
                                onChange={ e => setSection('invoice', 'email', e.target.value) }
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            label="Fakturadato"
                            type="date"
                            value={ form.invoice.invoiceDate }
                            onChange={ e => setSection('invoice', 'invoiceDate', e.target.value) }
                            InputLabelProps={{ shrink: true }}
                            sx={{ maxWidth: { sm: '50%' } }}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 2 }>
                            <FormControl fullWidth>
                                <InputLabel id="invoice-product-label">Produkt</InputLabel>
                                <Select
                                    labelId="invoice-product-label"
                                    label="Produkt"
                                    value={ form.invoice.productId }
                                    onChange={ e => selectProduct(e.target.value) }
                                >
                                    { productOptions.map(option => (
                                        <MenuItem key={ option.productId } value={ option.productId }>{ option.productName }</MenuItem>
                                    )) }
                                </Select>
                            </FormControl>
                            <TextField
                                label="Pris inkl. moms"
                                value={ form.invoice.amount }
                                onChange={ e => setSection('invoice', 'amount', e.target.value) }
                                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                                InputProps={{ endAdornment: <InputAdornment position="end">kr.</InputAdornment> }}
                                fullWidth
                            />
                        </Stack>
                        <FormControlLabel
                            control={ <Checkbox checked={ form.invoice.sendToBilly } onChange={ e => setSection('invoice', 'sendToBilly', e.target.checked) } /> }
                            label="Fakturer i Billy"
                        />
                    </Stack>
                ) }

                { status === 'notSold' && (
                    <FormGroup>
                        { notSoldReasonOptions.map(reason => (
                            <FormControlLabel
                                key={ reason.value }
                                control={ <Checkbox checked={ form.notSoldReasons.includes(reason.value) } onChange={ e => toggleReason(reason.value, e.target.checked) } /> }
                                label={ reason.name }
                            />
                        )) }
                    </FormGroup>
                ) }

                { error && <Alert severity="error" sx={{ mt: 2 }}>{ error }</Alert> }
            </DialogContent>
            <DialogActions>
                <Button onClick={ onClose } disabled={ saving }>Fortryd</Button>
                <Button
                    variant="contained"
                    color={ status === 'cancelled' ? 'error' : 'primary' }
                    onClick={ handleSubmit }
                    disabled={ saving || Boolean(validationError) }
                >
                    { submitLabel }
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default OrderStatusDialog;
